'use client';

import type { KeyboardEvent } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/libs/tailwind';
import { Label } from './Label';

type TextAreaProps = {
  name?: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  id?: string;
  rows?: number;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, value, onChange, onKeyDown, onKeyUp, placeholder, helperText, disabled, errorMessage, className, readOnly, ...props }, ref) => {
    return (
      <div className="flex flex-col w-full">
        {label && (
          <Label className={cn('mb-2 text-body-md font-medium text-fg-primary', errorMessage && 'text-interactive-negative')}>
            {label}
          </Label>
        )}
        <textarea
          name={props.name}
          ref={ref}
          value={value ?? undefined}
          onChange={e => onChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          className={cn(
            'min-h-[48px] w-full bg-bg-surface-primary text-fg-primary rounded-lg border-0.5 border-fg-border px-3 py-[11px] text-body-md outline-hidden box-border',
            'hover:border-fg-secondary placeholder:text-fg-secondary',
            'focus:border-1 focus:border-fg-primary focus:ring-0',
            'active:border-1 active:border-fg-primary',
            readOnly && 'bg-gray-50 cursor-not-allowed',
            disabled && 'bg-bg-disabled text-fg-tertiary hover:border-fg-border active:border-fg-border',
            errorMessage && 'border-interactive-negative hover:border-interactive-negative focus:border-interactive-negative active:border-interactive-negative',
            className,
          )}
          {...props}
        />
        {errorMessage && <p className="text-interactive-negative text-body-md mt-2">{errorMessage}</p>}
        {(helperText && !errorMessage) && <p className="text-fg-primary text-body-md mt-2">{helperText}</p>}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
